import { ModalLayout } from "moh-ui";
import useAuth from "@/hooks/useAuth";
import { useUserCouncil } from "@/hooks/useUserCouncil";
import React, { useState } from "react";
import { useRecoilState } from "recoil";
import { showScheduleExamModalState } from "@/atoms";
import { DateInput, TimeInput } from "@mantine/dates";
import { notifications } from "@mantine/notifications";
import { useCreateExamSchedule } from "@/services/exam-schedules";
import { QuestionSelection, ModuleSelection, UnitSelection } from "@/types/examination.types";
import { Button, Group, Text, Stack, Card, Switch, Divider, NumberInput, Select, TextInput } from "@mantine/core";
import { parseSubcenterValidationError, getSubcenterErrorMessage, isSubcenterValidationError } from "@/utils/subcenterErrorHandling";

interface ScheduleExamModalProps {
  // Add any additional props if needed
}

export default function ScheduleExamModal({}: ScheduleExamModalProps) {
  const [show, setShow] = useRecoilState(showScheduleExamModalState);
  const createExamSchedule = useCreateExamSchedule();
  const { user } = useAuth();
  const council = useUserCouncil();
  
  const [formData, setFormData] = useState({
    title: "",
    startDate: "",
    startTime: "",
    duration: 180,
    examCenterSubCenters: {} as Record<string, string[]>, // 🆕 New structure
    published: false,
    public: false,
    allowedInstitutionIds: [] as string[], // 🆕 Institution IDs allowed to access private exam
    retakeSeatsAllocated: 0, // 🆕 Number of seats allocated for retake attempts
    councilId: council?.id || "",
    categoryId: "", // Add categoryId field
    questionSelection: undefined as QuestionSelection | undefined,
  });

  const [questionSelectionMode, setQuestionSelectionMode] = useState<'random' | 'targeted'>('random');
  const [targetedSelection, setTargetedSelection] = useState<QuestionSelection>({
    moduleSelections: [],
    unitSelections: [],
  });

  const handleSubmit = async () => {
    try {
      const requestData = {
        ...formData,
        educationProgramId: formData.categoryId, // Map categoryId to educationProgramId
        examCenterSubCenters: formData.examCenterSubCenters, // 🆕 New structure
        questionSelection: questionSelectionMode === 'targeted' ? targetedSelection : undefined,
        // 🆕 Enhanced fields for exam schedules
        allowedInstitutionIds: formData.public ? [] : formData.allowedInstitutionIds,
        retakeSeatsAllocated: formData.retakeSeatsAllocated,
      };

      await createExamSchedule.mutateAsync(requestData);
      
      notifications.show({
        title: 'Success',
        message: 'Exam schedule created successfully',
        color: 'green',
      });
      
      setShow(null);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to create exam schedule';
      
      // Handle subcenter-specific validation errors
      if (isSubcenterValidationError(errorMessage)) {
        const subcenterError = parseSubcenterValidationError(errorMessage);
        const friendlyMessage = getSubcenterErrorMessage(subcenterError);
        
        notifications.show({
          title: 'Sub-center Validation Error',
          message: friendlyMessage,
          color: 'red',
          autoClose: 8000, // Show longer for complex messages
        });
      } else {
        notifications.show({
          title: 'Error',
          message: errorMessage,
          color: 'red',
        });
      }
    }
  };

  const addModuleSelection = () => {
    setTargetedSelection((prev: QuestionSelection) => ({
      ...prev,
      moduleSelections: [...(prev.moduleSelections || []), {
        moduleId: "",
        questionCount: 0,
        bloomsDistribution: {},
      }],
    }));
  };

  const addUnitSelection = () => {
    setTargetedSelection((prev: QuestionSelection) => ({
      ...prev,
      unitSelections: [...(prev.unitSelections || []), {
        unitId: "",
        questionCount: 0,
        bloomsDistribution: {},
      }],
    }));
  };

  const updateModuleSelection = (index: number, field: keyof ModuleSelection, value: any) => {
    setTargetedSelection((prev: QuestionSelection) => ({
      ...prev,
      moduleSelections: prev.moduleSelections?.map((module: ModuleSelection, i: number) => 
        i === index ? { ...module, [field]: value } : module
      ),
    }));
  };

  const updateUnitSelection = (index: number, field: keyof UnitSelection, value: any) => {
    setTargetedSelection((prev: QuestionSelection) => ({
      ...prev,
      unitSelections: prev.unitSelections?.map((unit: UnitSelection, i: number) => 
        i === index ? { ...unit, [field]: value } : unit
      ),
    }));
  };

  const removeModuleSelection = (index: number) => {
    setTargetedSelection((prev: QuestionSelection) => ({
      ...prev,
      moduleSelections: prev.moduleSelections?.filter((_: ModuleSelection, i: number) => i !== index),
    }));
  };

  const removeUnitSelection = (index: number) => {
    setTargetedSelection((prev: QuestionSelection) => ({
      ...prev,
      unitSelections: prev.unitSelections?.filter((_: UnitSelection, i: number) => i !== index),
    }));
  };

  const totalSelectedQuestions = (targetedSelection.moduleSelections?.reduce((sum: number, m: ModuleSelection) => sum + m.questionCount, 0) || 0) +
                               (targetedSelection.unitSelections?.reduce((sum: number, u: UnitSelection) => sum + u.questionCount, 0) || 0);

  return (
    <ModalLayout 
      open={Boolean(show?.show)} 
      onClose={() => setShow(null)}
      size="xl"
    >
      <div className="p-6">
        <Stack gap="lg">
          <Text size="xl" fw={600}>Schedule Exam</Text>
          
          {/* Basic Information */}
          <Card p="md" withBorder>
            <Text size="lg" fw={500} mb="md">Basic Information</Text>
            <Stack gap="md">
              <TextInput
                label="Exam Title"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                required
              />
              
              
              <Group grow>
                <DateInput
                  label="Start Date"
                  value={formData.startDate ? new Date(formData.startDate) : null}
                  onChange={(date) => setFormData(prev => ({ 
                    ...prev, 
                    startDate: date ? date.toISOString().split('T')[0] : "" 
                  }))}
                  required
                />
                <TimeInput
                  label="Start Time"
                  value={formData.startTime}
                  onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
                  required
                />
              </Group>
              
              <NumberInput
                label="Duration (minutes)"
                value={formData.duration}
                onChange={(value) => setFormData(prev => ({ ...prev, duration: typeof value === 'number' ? value : 0 }))}
                min={1}
                required
              />
              
              <Text size="sm" c="dimmed">
                Exam centers will be selected during the exam schedule creation process.
              </Text>
            </Stack>
          </Card>

          {/* Visibility Settings */}
          <Card p="md" withBorder>
            <Text size="lg" fw={500} mb="md">Visibility Settings</Text>
            <Stack gap="md">
              <Switch
                label="Published"
                checked={formData.published}
                onChange={(e) => setFormData(prev => ({ ...prev, published: e.target.checked }))}
              />
              <Switch
                label="Public Schedule"
                checked={formData.public}
                onChange={(e) => setFormData(prev => ({ ...prev, public: e.target.checked }))}
                description="Make this exam schedule visible to all institutions with the education program"
              />
              
              {/* 🆕 Private Exam Schedule Settings */}
              {!formData.public && (
                <Stack gap="md" mt="md">
                  <Divider label="Private Exam Settings" />
                  <NumberInput
                    label="Retake Seats Allocated"
                    value={formData.retakeSeatsAllocated}
                    onChange={(value) => setFormData(prev => ({ 
                      ...prev, 
                      retakeSeatsAllocated: typeof value === 'number' ? value : 0 
                    }))}
                    min={0}
                    description="Number of seats reserved for retake attempts"
                    required
                  />
                  <Text size="sm" c="dimmed">
                    For private exam schedules, you can specify which institutions have access and how many retake seats are available.
                  </Text>
                </Stack>
              )}
            </Stack>
          </Card>

          {/* Question Selection */}
          <Card p="md" withBorder>
            <Text size="lg" fw={500} mb="md">Question Selection</Text>
            <Stack gap="md">
              <Select
                label="Selection Mode"
                value={questionSelectionMode}
                onChange={(value) => setQuestionSelectionMode(value as 'random' | 'targeted')}
                data={[
                  { value: 'random', label: 'Random Selection' },
                  { value: 'targeted', label: 'Targeted Selection' },
                ]}
                description={questionSelectionMode === 'random' 
                  ? "System will randomly select questions from available pool"
                  : "Specify exact module/unit distribution with optional Bloom's taxonomy targeting"
                }
              />

              {questionSelectionMode === 'targeted' && (
                <Stack gap="md">
                  <Text size="sm" fw={500}>Targeted Selection Configuration</Text>
                  
                  <Divider label="Module Selections" />
                  
                  {targetedSelection.moduleSelections?.map((module: ModuleSelection, index: number) => (
                    <Card key={index} p="sm" withBorder>
                      <Group justify="space-between" mb="sm">
                        <Text size="sm" fw={500}>Module {index + 1}</Text>
                        <Button 
                          size="xs" 
                          color="red" 
                          variant="light"
                          onClick={() => removeModuleSelection(index)}
                        >
                          Remove
                        </Button>
                      </Group>
                      <Stack gap="sm">
                        <TextInput
                          label="Module ID"
                          value={module.moduleId}
                          onChange={(e) => updateModuleSelection(index, 'moduleId', e.target.value)}
                          placeholder="Enter module ID"
                        />
                        <NumberInput
                          label="Question Count"
                          value={module.questionCount}
                          onChange={(value) => updateModuleSelection(index, 'questionCount', typeof value === 'number' ? value : 0)}
                          min={1}
                        />
                      </Stack>
                    </Card>
                  ))}
                  
                  <Button 
                    size="sm" 
                    variant="light" 
                    onClick={addModuleSelection}
                  >
                    Add Module Selection
                  </Button>

                  <Divider label="Unit Selections" />
                  
                  {targetedSelection.unitSelections?.map((unit: UnitSelection, index: number) => (
                    <Card key={index} p="sm" withBorder>
                      <Group justify="space-between" mb="sm">
                        <Text size="sm" fw={500}>Unit {index + 1}</Text>
                        <Button 
                          size="xs" 
                          color="red" 
                          variant="light"
                          onClick={() => removeUnitSelection(index)}
                        >
                          Remove
                        </Button>
                      </Group>
                      <Stack gap="sm">
                        <TextInput
                          label="Unit ID"
                          value={unit.unitId}
                          onChange={(e) => updateUnitSelection(index, 'unitId', e.target.value)}
                          placeholder="Enter unit ID"
                        />
                        <NumberInput
                          label="Question Count"
                          value={unit.questionCount}
                          onChange={(value) => updateUnitSelection(index, 'questionCount', typeof value === 'number' ? value : 0)}
                          min={1}
                        />
                      </Stack>
                    </Card>
                  ))}
                  
                  <Button 
                    size="sm" 
                    variant="light" 
                    onClick={addUnitSelection}
                  >
                    Add Unit Selection
                  </Button>
                </Stack>
              )}
            </Stack>
          </Card>

          {/* Actions */}
          <Group justify="flex-end" gap="md">
            <Button 
              variant="light" 
              onClick={() => setShow(null)}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              loading={createExamSchedule.isPending}
              disabled={!formData.title || !formData.startDate || !formData.startTime}
            >
              Create Exam Schedule
            </Button>
          </Group>
        </Stack>
      </div>
    </ModalLayout>
  );
}
